import { DEFAULT_PERIOD_WEEKS, PERIOD_PRESETS } from "../constants"
import {
  CalendarDay,
  DayKey,
  MONTH_NAMES,
  MONTH_NAMES_GEN,
  calendarDayFromEpochDay,
  dayKeyFromEpochDay,
  epochDayFromDayKey,
  startOfIsoWeek,
  todayEpochDayMsk,
} from "./mskCalendar"

export { DEFAULT_PERIOD_WEEKS, PERIOD_PRESETS }

export interface DayColumn extends CalendarDay {
  isWeekend: boolean
  isToday: boolean
}

export interface Period {
  startEpochDay: number
  weeks: number
  dateFrom: DayKey
  dateTo: DayKey
  days: DayColumn[]
}

/**
 * Период всегда состоит из целых недель Пн–Вс — неполных недель на краях не бывает.
 * `now` пробрасывается только в тестах.
 */
export const buildPeriod = (
  startEpochDay: number,
  weeks: number,
  now: number = Date.now(),
): Period => {
  const monday = startOfIsoWeek(startEpochDay)
  const today = todayEpochDayMsk(now)
  const days: DayColumn[] = []
  for (let i = 0; i < weeks * 7; i++) {
    const cd = calendarDayFromEpochDay(monday + i)
    days.push({
      ...cd,
      isWeekend: cd.weekday >= 6,
      isToday: cd.epochDay === today,
    })
  }
  return {
    startEpochDay: monday,
    weeks,
    dateFrom: days[0].key,
    dateTo: days[days.length - 1].key,
    days,
  }
}

export interface HeaderTiers {
  /**
   * По одному блоку на неделю, span всегда 7. Подпись месяца — от месяца ПОНЕДЕЛЬНИКА
   * недели (неделя 31 на референсе — «Июль»), одинаковые соседние подписи не сливаются.
   */
  weeks: { isoWeek: number; monthLabel: string }[]
  yearLabel: string
}

export const buildHeaderTiers = (days: DayColumn[]): HeaderTiers => {
  const weeks: HeaderTiers["weeks"] = []
  for (let i = 0; i < days.length; i += 7) {
    const monday = days[i]
    weeks.push({
      isoWeek: monday.isoWeek,
      monthLabel: MONTH_NAMES[monday.month - 1],
    })
  }
  const firstYear = days[0].year
  const lastYear = days[days.length - 1].year
  return {
    weeks,
    yearLabel:
      firstYear === lastYear ? String(firstYear) : `${firstYear}–${lastYear}`,
  }
}

/** Разбивка диапазона на недельные поддиапазоны — для queryFn списка (раздел 6 спеки). */
export const splitRangeIntoWeeks = (
  dateFrom: DayKey,
  dateTo: DayKey,
): { from: DayKey; to: DayKey }[] => {
  const start = epochDayFromDayKey(dateFrom)
  const end = epochDayFromDayKey(dateTo)
  const ranges: { from: DayKey; to: DayKey }[] = []
  for (let e = start; e <= end; e += 7) {
    ranges.push({
      from: dayKeyFromEpochDay(e),
      to: dayKeyFromEpochDay(Math.min(e + 6, end)),
    })
  }
  return ranges
}

/**
 * Аргументы кэша schedulesList. ЕДИНСТВЕННЫЙ строитель: updateQueryData находит запись
 * кэша через serializeQueryArgs, поэтому useScheduleData (подписка) и usePaintCommit
 * (патч) обязаны использовать тождественные аргументы — иначе патч молча станет no-op.
 */
export interface ScheduleListArgs {
  date_from: DayKey
  date_to: DayKey
}

export const buildScheduleListArgs = (period: Period): ScheduleListArgs => ({
  date_from: period.dateFrom,
  date_to: period.dateTo,
})

/** «13 июля — 9 августа 2026» (год — по последнему дню, он же определяет и первый ярус). */
export const formatPeriodLabel = (period: Period): string => {
  const first = period.days[0]
  const last = period.days[period.days.length - 1]
  return `${first.day} ${MONTH_NAMES_GEN[first.month - 1]} — ${last.day} ${
    MONTH_NAMES_GEN[last.month - 1]
  } ${last.year}`
}
